// src/pages/EmployeeTasksPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, query, where, orderBy, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase'; // Adjust path as needed
import { useAuth } from '../context/AuthContext'; // For role-based access if needed later
import { formatDate } from '../utils/styleUtils'; // Assuming you have this utility
import styles from '../styles/EmployeeTasksPage.module.css'; // Create this CSS module

const EmployeeTasksPage = () => {
    const { employeeId } = useParams(); // Get employeeId (UID) from URL
    const { currentUser, userRole } = useAuth(); // For potential future permission checks

    const [employeeDetails, setEmployeeDetails] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [loadingEmployee, setLoadingEmployee] = useState(true);
    const [loadingTasks, setLoadingTasks] = useState(true);
    const [error, setError] = useState('');

    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    // Fetch Employee Details
    useEffect(() => {
        if (!employeeId) {
            setError("Employee ID is missing.");
            setLoadingEmployee(false);
            return;
        }
        setLoadingEmployee(true);
        const employeeDocRef = doc(db, 'users', employeeId);

        const fetchEmployeeData = async () => {
            try {
                const docSnap = await getDoc(employeeDocRef);
                if (docSnap.exists()) {
                    if (isMounted.current) {
                        setEmployeeDetails({ id: docSnap.id, ...docSnap.data() });
                    }
                } else {
                    if (isMounted.current) setError("Employee not found.");
                }
            } catch (err) {
                console.error("Error fetching employee details:", err);
                if (isMounted.current) setError("Failed to load employee details.");
            } finally {
                if (isMounted.current) setLoadingEmployee(false);
            }
        };

        fetchEmployeeData();
        // No direct unsubscribe needed for a single getDoc, but isMounted handles component unmount
    }, [employeeId]);

    // Fetch Tasks for the Employee
    useEffect(() => {
        if (!employeeId) {
            setLoadingTasks(false);
            return; // Don't fetch if no employeeId
        }

        setLoadingTasks(true);
        setError(''); // Clear previous errors

        const tasksCollectionRef = collection(db, 'tasks');
        const q = query(
            tasksCollectionRef,
            where('assignedToUid', '==', employeeId),
            orderBy('createdAt', 'desc') // Show newest tasks first
        );

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            if (!isMounted.current) return;
            const fetchedTasks = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                dueDate: doc.data().dueDate?.toDate(),
                createdAt: doc.data().createdAt?.toDate(),
            }));
            setTasks(fetchedTasks);
            setLoadingTasks(false);
        }, (err) => {
            if (!isMounted.current) return;
            console.error("Error fetching employee tasks:", err);
            setError("Failed to load tasks for this employee.");
            setLoadingTasks(false);
        });

        return () => unsubscribe(); // Cleanup listener on component unmount
    }, [employeeId]);

    if (loadingEmployee) {
        return <div className={styles.loadingIndicator}>Loading employee details...</div>;
    }

    if (error && !employeeDetails) { // Show critical error if employee details failed to load
        return <div className={`${styles.pageContainer} ${styles.errorMessage}`}>{error}</div>;
    }
    
    return (
        <div className={styles.pageContainer}>
            <div className={styles.header}>
                {employeeDetails ? (
                    <h1 className={styles.title}>
                        Tasks for {employeeDetails.name || 'Employee'}
                        <span className={styles.employeeMeta}> ({employeeDetails.email} - {employeeDetails.role})</span>
                    </h1>
                ) : (
                    <h1 className={styles.title}>Employee Tasks</h1>
                )}
                <Link to="/employees" className={styles.backButton}>Back to Employee List</Link>
            </div>

            {error && <div className={styles.errorMessage}>{error}</div>}
            
            {loadingTasks ? (
                <div className={styles.loadingIndicator}>Loading tasks...</div>
            ) : tasks.length === 0 ? (
                <p className={styles.noDataMessage}>No tasks found for this employee.</p>
            ) : (
                <div className={styles.tasksListContainer}>
                    <ul className={styles.taskList}>
                        {tasks.map(task => (
                            <li key={task.id} className={styles.taskItem}>
                                <div className={styles.taskItemHeader}>
                                    <Link to={`/task/${task.id}`} className={styles.taskTitleLink}>
                                        <h3>{task.title || 'Untitled Task'}</h3>
                                    </Link>
                                    <span className={`${styles.statusBadge} ${styles[`status${task.status?.replace(/\s+/g, '')}`]}`}>
                                        {task.status || 'N/A'}
                                    </span>
                                </div>
                                <p className={styles.taskMeta}>
                                    <strong>Priority:</strong> {task.priority || 'N/A'}
                                </p>
                                {task.dueDate && (
                                    <p className={styles.taskMeta}>
                                        <strong>Due Date:</strong> {formatDate(task.dueDate)}
                                    </p>
                                )}
                                <p className={styles.taskMeta}>
                                    <strong>Created:</strong> {task.createdAt ? formatDate(task.createdAt) : 'N/A'}
                                </p>
                                {task.description && (
                                    <p className={styles.taskDescriptionPreview}>
                                        {task.description.substring(0, 100)}{task.description.length > 100 ? '...' : ''}
                                    </p>
                                )}
                                <Link to={`/task/${task.id}`} className={styles.viewDetailsButton}>
                                    View Details
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default EmployeeTasksPage;
